import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, getDoc, where } from 'firebase/firestore';
import type { Task, TaskPriority } from '@allemnii-pulse/shared';
import { useCollectionGroup } from '../../lib/firestore.js';
import { db } from '../../lib/firebase.js';
import { sortTasksByPriority } from '../../lib/schedule.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Card } from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { StatusBadge } from '../../components/ui/StatusBadge.js';
import { Checkbox } from '../../components/ui/Checkbox.js';
import { Label } from '../../components/ui/Label.js';

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const PRIORITY_VAR: Record<TaskPriority, string> = {
  critical: '--danger',
  high: '--warning',
  medium: '--ink-400',
  low: '--ink-300',
};

function PriorityBadge({ priority }: { priority: TaskPriority }) {
  return (
    <Badge
      className="border-transparent"
      style={{
        color: `var(${PRIORITY_VAR[priority]})`,
        backgroundColor: `color-mix(in srgb, var(${PRIORITY_VAR[priority]}) 16%, transparent)`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-sm"
        style={{ backgroundColor: `var(${PRIORITY_VAR[priority]})` }}
      />
      {PRIORITY_LABEL[priority]}
    </Badge>
  );
}

/** One-time lookup of project names for grouping — like lib/userProfiles.ts's useUserProfiles,
 * but for the small, fairly static set of project ids a person's assigned tasks span. */
function useProjectNames(ids: string[]): Record<string, string> {
  const [names, setNames] = useState<Record<string, string>>({});
  const key = ids.join(',');

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      ids.map(async (id) => {
        const snap = await getDoc(doc(db, 'projects', id));
        return [id, snap.exists() ? ((snap.data().name as string) ?? id) : id] as const;
      }),
    ).then((entries) => {
      if (!cancelled) setNames(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
    // `key` stands in for the id list's real identity so this doesn't re-fetch on every render.
  }, [key]);

  return names;
}

function isOverdue(task: Task): boolean {
  if (!task.dueDate || task.status === 'done' || task.status === 'canceled') return false;
  return task.dueDate < new Date().toISOString().slice(0, 10);
}

/** Every task assigned to me, across every project — a Firestore collectionGroup query (see
 * firestore.rules — the tasks match block needs a self-contained clause for this to be a
 * provably-safe query, not just an ancestor-lookup one). Grouped by project and ordered by
 * priority within each, since "my tasks" spans projects with unrelated dependency graphs. */
export function MyTasksPage() {
  const { user } = useAuth();
  const constraints = useMemo(() => (user ? [where('assigneeUid', '==', user.uid)] : []), [user]);
  const { data: tasks, loading, error } = useCollectionGroup<Task>('tasks', constraints);
  const [hideCompleted, setHideCompleted] = useState(true);

  const projectIds = useMemo(
    () =>
      Array.from(new Set(tasks.map((t) => t.parentId).filter((id): id is string => Boolean(id)))),
    [tasks],
  );
  const projectNames = useProjectNames(projectIds);

  const overdueCount = tasks.filter(isOverdue).length;
  const visibleTasks = hideCompleted
    ? tasks.filter((t) => t.status !== 'done' && t.status !== 'canceled')
    : tasks;

  const groups = useMemo(() => {
    const byProject = new Map<string, Task[]>();
    for (const t of visibleTasks) {
      const key = t.parentId ?? 'unknown';
      const arr = byProject.get(key) ?? [];
      arr.push(t);
      byProject.set(key, arr);
    }
    return Array.from(byProject.entries())
      .map(([projectId, projectTasks]) => ({
        projectId,
        name: projectNames[projectId] ?? 'Untitled project',
        tasks: sortTasksByPriority(projectTasks),
        overdue: projectTasks.filter(isOverdue).length,
      }))
      .sort((a, b) => b.overdue - a.overdue || a.name.localeCompare(b.name));
    // `visibleTasks`'s identity changes with `tasks`/`hideCompleted`; `projectNames` fills in async.
  }, [visibleTasks, projectNames]);

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-lg font-semibold">My tasks</h1>
          <p className="text-sm text-muted">Everything assigned to you, across every project.</p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          {overdueCount > 0 && (
            <span className="text-xs font-medium text-danger">{overdueCount} overdue</span>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="hide-completed"
              checked={hideCompleted}
              onCheckedChange={(v) => setHideCompleted(v === true)}
            />
            <Label htmlFor="hide-completed" className="text-xs font-normal text-muted">
              Hide completed
            </Label>
          </div>
        </div>
      </div>

      {error ? (
        <Card className="p-6 text-center text-sm text-danger sm:p-8">
          Couldn't load your tasks: {error.message}
        </Card>
      ) : loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : groups.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted sm:p-8">
          {tasks.length === 0
            ? 'Nothing assigned to you right now.'
            : "You're all caught up — every assigned task is done."}
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.projectId} className="flex flex-col gap-2">
              <div className="flex items-center gap-2 px-1">
                <Link
                  to={`/projects/${group.projectId}`}
                  className="min-w-0 truncate text-xs font-semibold uppercase tracking-wide text-muted hover:text-text"
                >
                  {group.name}
                </Link>
                <span className="flex-shrink-0 text-[10px] text-muted">({group.tasks.length})</span>
                {group.overdue > 0 && (
                  <span className="flex-shrink-0 text-[10px] font-medium text-danger">
                    {group.overdue} overdue
                  </span>
                )}
              </div>
              <Card className="flex flex-col divide-y divide-border">
                {group.tasks.map((t) => {
                  const overdue = isOverdue(t);
                  return (
                    <Link
                      key={t.id}
                      to={`/projects/${group.projectId}/tasks/${t.id}`}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 p-3 hover:bg-surface-2 md:flex-nowrap"
                    >
                      {/* Its own line on phones, so the due date and badges don't squeeze it to nothing. */}
                      <span className="min-w-0 basis-full truncate text-sm md:flex-1">{t.title}</span>
                      {t.dueDate && (
                        <span
                          className={`text-xs ${overdue ? 'font-medium text-danger' : 'text-muted'}`}
                        >
                          {overdue ? 'Overdue ' : 'Due '}
                          {new Date(`${t.dueDate}T00:00:00`).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                      <PriorityBadge priority={t.priority} />
                      <StatusBadge status={t.status} />
                    </Link>
                  );
                })}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
